import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTheme } from '../../../../Context/ThemeContext';
import { PlayerHandler, getCachedPlayers, fetchAllPlayersWithCache } from '../PlayerHandler';
import { DashboardHandler } from '../../Dashboard/DashboardHandler';
import { SlimPlayer, IFilter, FilterType, JoinType } from '../../../../Core/Api/Api';
import TableRequest from '../../../../Core/TableUtility/Models/TableRequest';

interface PlayerListViewProps {
  onViewPlayer?: (player: SlimPlayer) => void;
}

const PAGE_SIZE_OPTIONS = [10, 20, 50];

const PlayerListView: React.FC<PlayerListViewProps> = ({ onViewPlayer }) => {
  const { theme, isMobile } = useTheme();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize state from URL search params
  const initialPage = parseInt(searchParams.get('page') || '1', 10) || 1;
  const initialPageSize = parseInt(searchParams.get('size') || '10', 10) || 10;
  const initialSearch = searchParams.get('search') || '';
  const initialSortCol = searchParams.get('sortCol') || null;
  const initialSortDir = (searchParams.get('sortDir') as 'asc' | 'desc' | null) || null;

  // Pagination state
  const [players, setPlayers] = useState<SlimPlayer[]>([]);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(initialSearch);
  const [sortColumn, setSortColumn] = useState<string | null>(initialSortCol);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc' | null>(initialSortDir);

  // Progressive enhancement: full dataset for client-side operations
  const [allPlayers, setAllPlayers] = useState<SlimPlayer[] | null>(null);
  const [isClientSide, setIsClientSide] = useState(false);

  // Sync filter state to URL search params (replace, not push, to avoid polluting history)
  const syncSearchParams = useCallback((overrides: { page?: number; size?: number; search?: string; sortCol?: string | null; sortDir?: string | null }) => {
    const params = new URLSearchParams();
    const page = overrides.page ?? currentPage;
    const size = overrides.size ?? pageSize;
    const search = overrides.search ?? searchTerm;
    const col = overrides.sortCol !== undefined ? overrides.sortCol : sortColumn;
    const dir = overrides.sortDir !== undefined ? overrides.sortDir : sortOrder;

    if (page > 1) params.set('page', String(page));
    if (size !== 10) params.set('size', String(size));
    if (search) params.set('search', search);
    if (col) params.set('sortCol', col);
    if (dir) params.set('sortDir', dir);

    setSearchParams(params, { replace: true });
  }, [currentPage, pageSize, searchTerm, sortColumn, sortOrder, setSearchParams]);

  // Debounce ref for search
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const bgFetchControllerRef = useRef<AbortController | null>(null);

  // Stats from dashboard
  const [onlinePlayers, setOnlinePlayers] = useState<number>(0);
  const [totalGames, setTotalGames] = useState<number>(0);
  const [activePlayers, setActivePlayers] = useState<number>(0);
  const [registeredCount, setRegisteredCount] = useState<number>(0);
  const [guestCount, setGuestCount] = useState<number>(0);

  // Fetch players with pagination
  const fetchPlayers = useCallback(async (page: number, search: string, sortCol: string | null, sortDir: 'asc' | 'desc' | null, perPage: number) => {
    setLoading(true);
    try {
      const playerHandler = new PlayerHandler();
      const request = new TableRequest();
      request.pageNumber = page;
      request.amountPerPage = perPage;

      // Add search filter if present - search across multiple fields with OR logic
      if (search) {
        request.filters = [
          {
            filterType: FilterType.Group,
            groupFilters: [
              { field: 'Username', filterType: FilterType.Contains, value: search },
              { field: 'Email', filterType: FilterType.Contains, value: search, joinType: JoinType.Or },
            ],
          } as IFilter,
        ];
      }

      // Add sorting if present
      if (sortCol && sortDir) {
        request.orderBy = [
          { field: sortCol, ascending: sortDir === 'asc' }
        ];
      }

      const response = await playerHandler.GetData(request);
      setPlayers(response.results || []);
      setTotalCount(response.count || 0);
    } catch (error) {
      console.error('Error fetching players:', error);
      setPlayers([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, []);

  // Background fetch of ALL players for client-side mode (uses module-level cache)
  const fetchAllPlayers = useCallback(async (signal: AbortSignal) => {
    try {
      const results = await fetchAllPlayersWithCache(signal);
      if (signal.aborted) return;
      setAllPlayers(results);
      setIsClientSide(true);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      console.warn('Background fetch failed, staying in server-side mode:', error);
    }
  }, []);

  // Initial load - fetch dashboard stats and first page of players
  useEffect(() => {
    const fetchDashboardStats = async () => {
      try {
        const dashboardHandler = new DashboardHandler();
        const [dashboardData, userCountData] = await Promise.all([
          dashboardHandler.getDashboardData(),
          dashboardHandler.getUserCount(),
        ]);

        const totalGamesCard = dashboardData.gameCards?.find(c => c.title === 'Total Games');
        const activeCard = dashboardData.playerCards?.find(c => c.title === 'Active Players');
        const registeredCard = dashboardData.playerCards?.find(c => c.title === 'Total Players');
        const guestCard = dashboardData.playerCards?.find(c => c.title === 'Total Guests');
        setTotalGames(totalGamesCard?.count ?? 0);
        setActivePlayers(activeCard?.count ?? 0);
        setOnlinePlayers(userCountData?.count ?? 0);
        setRegisteredCount(registeredCard?.count ?? 0);
        setGuestCount(guestCard?.count ?? 0);
      } catch (error) {
        console.error('Error fetching dashboard stats:', error);
      }
    };

    fetchDashboardStats();

    // Check module-level cache first — skip network calls if warm
    const cached = getCachedPlayers();
    if (cached) {
      setAllPlayers(cached);
      setIsClientSide(true);
      setLoading(false);
      return;
    }

    // Cache miss — fetch first page for immediate display, then background-fetch all
    fetchPlayers(initialPage, initialSearch, initialSortCol, initialSortDir, initialPageSize);

    const controller = new AbortController();
    bgFetchControllerRef.current = controller;
    fetchAllPlayers(controller.signal);

    return () => { controller.abort(); };
  }, [fetchPlayers, fetchAllPlayers, pageSize]);

  // Handle search with debounce
  const handleSearch = (value: string) => {
    setSearchTerm(value);

    if (isClientSide) {
      // Client-side: useMemo reacts instantly, just reset page
      setCurrentPage(1);
      syncSearchParams({ search: value, page: 1 });
    } else {
      // Server-side: debounced fetch
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      searchTimeoutRef.current = setTimeout(() => {
        setCurrentPage(1);
        syncSearchParams({ search: value, page: 1 });
        fetchPlayers(1, value, sortColumn, sortOrder, pageSize);
      }, 300);
    }
  };

  // Handle sorting
  const handleSort = (column: string) => {
    let newOrder: 'asc' | 'desc' | null = 'asc';

    if (sortColumn === column) {
      if (sortOrder === 'asc') newOrder = 'desc';
      else if (sortOrder === 'desc') newOrder = null;
    }

    const newCol = newOrder ? column : null;
    setSortColumn(newCol);
    setSortOrder(newOrder);
    setCurrentPage(1);
    syncSearchParams({ sortCol: newCol, sortDir: newOrder, page: 1 });

    if (!isClientSide) {
      fetchPlayers(1, searchTerm, newCol, newOrder, pageSize);
    }
  };

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    syncSearchParams({ page });
    if (!isClientSide) {
      fetchPlayers(page, searchTerm, sortColumn, sortOrder, pageSize);
    }
  };

  // Handle page size change
  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(1);
    syncSearchParams({ size: newSize, page: 1 });
    if (!isClientSide) {
      fetchPlayers(1, searchTerm, sortColumn, sortOrder, newSize);
    }
  };

  // Client-side filtering, sorting, and pagination
  const clientSideData = useMemo(() => {
    if (!isClientSide || !allPlayers) return null;

    // Filter
    let filtered = allPlayers;
    if (searchTerm) {
      const lower = searchTerm.toLowerCase();
      filtered = allPlayers.filter(p =>
        (p.username && p.username.toLowerCase().includes(lower)) ||
        (p.email && p.email.toLowerCase().includes(lower))
      );
    }

    // Sort
    if (sortColumn && sortOrder) {
      const dir = sortOrder === 'asc' ? 1 : -1;
      filtered = [...filtered].sort((a, b) => {
        const valA = (a as any)[sortColumn];
        const valB = (b as any)[sortColumn];

        if (valA == null && valB == null) return 0;
        if (valA == null) return 1;
        if (valB == null) return -1;

        if (sortColumn === 'lastActivityTime') {
          return (new Date(valA).getTime() - new Date(valB).getTime()) * dir;
        }
        if (sortColumn === 'rankName') {
          return ((a as any).rankScore - (b as any).rankScore) * dir;
        }
        if (typeof valA === 'number' && typeof valB === 'number') {
          return (valA - valB) * dir;
        }
        return String(valA).toLowerCase().localeCompare(String(valB).toLowerCase()) * dir;
      });
    }

    // Paginate
    const startIndex = (currentPage - 1) * pageSize;
    return { players: filtered.slice(startIndex, startIndex + pageSize), totalCount: filtered.length };
  }, [isClientSide, allPlayers, searchTerm, sortColumn, sortOrder, currentPage, pageSize]);

  // Derive display data: client-side when available, server-side otherwise
  const displayPlayers = clientSideData ? clientSideData.players : players;
  const displayTotalCount = clientSideData ? clientSideData.totalCount : totalCount;
  const displayTotalPages = Math.ceil(displayTotalCount / pageSize);

  const getRankColor = (rank: string) => {
    switch (rank) {
      case 'Diamond': return { bg: 'rgba(96, 165, 250, 0.2)', color: '#60a5fa' };
      case 'Platinum': return { bg: 'rgba(168, 162, 158, 0.2)', color: '#a8a29e' };
      case 'Gold': return { bg: 'rgba(250, 204, 21, 0.2)', color: '#facc15' };
      case 'Silver': return { bg: 'rgba(156, 163, 175, 0.2)', color: '#9ca3af' };
      case 'Bronze': return { bg: 'rgba(217, 119, 6, 0.2)', color: '#d97706' };
      default: return { bg: 'rgba(107, 114, 128, 0.2)', color: '#6b7280' };
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Online': return { bg: 'rgba(34, 197, 94, 0.2)', color: '#22c55e' };
      case 'Away': return { bg: 'rgba(250, 204, 21, 0.2)', color: '#facc15' };
      case 'Offline': return { bg: 'rgba(107, 114, 128, 0.2)', color: '#6b7280' };
      default: return { bg: 'rgba(107, 114, 128, 0.2)', color: '#6b7280' };
    }
  };

  const columns = [
    { key: 'username', title: 'Username', sortable: true, width: '12%' },
    { key: 'email', title: 'Email', sortable: true, width: '18%' },
    { key: 'lastActivityTime', title: 'Last Logged In', sortable: true, width: '13%' },
    { key: 'status', title: 'Status', sortable: true, width: '10%' },
    { key: 'rankName', title: 'Rank', sortable: true, width: '11%' },
    { key: 'hints', title: 'Hints', sortable: true, width: '8%' },
    { key: 'boards', title: 'Boards', sortable: true, width: '8%' },
    { key: 'moreTime', title: 'More Time', sortable: true, width: '8%' },
    { key: 'actions', title: 'Actions', sortable: false, width: '8%' },
  ];

  const styles: Record<string, React.CSSProperties> = {
    container: { padding: 0 },
    header: { marginBottom: '24px' },
    title: { fontSize: isMobile ? '24px' : '28px', fontWeight: 700, color: theme.titleColor, marginBottom: '8px' },
    subtitle: { fontSize: '14px', color: theme.textSecondary, marginBottom: '16px' },
    statsRow: { display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(5, 1fr)', gap: '10px', marginBottom: '24px' },
    statCard: { background: theme.card, borderRadius: '8px', padding: '12px 14px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
    statLabel: { fontSize: '12px', color: theme.textSecondary, marginBottom: '4px' },
    statValue: { fontSize: '20px', fontWeight: 700, color: theme.text },
    statSubtext: { fontSize: '12px', color: theme.textSecondary, marginTop: '4px' },
    tableContainer: { background: theme.card, borderRadius: '8px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
    searchContainer: { padding: '16px', borderBottom: `1px solid ${theme.border}` },
    searchInput: { width: '100%', maxWidth: '300px', padding: '10px 14px', fontSize: '14px', border: `1px solid ${theme.inputBorder}`, borderRadius: '6px', background: theme.inputBg, color: theme.text, outline: 'none' },
    tableWrapper: { overflowX: 'auto' },
    table: { width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' as const, minWidth: isMobile ? '800px' : 'auto' },
    th: { padding: '14px 16px', textAlign: 'center' as const, fontWeight: 600, fontSize: '13px', color: theme.textSecondary, background: theme.tableHeader, borderBottom: `1px solid ${theme.border}`, cursor: 'pointer', userSelect: 'none' as const, whiteSpace: 'nowrap' as const },
    td: { padding: '14px 16px', textAlign: 'center' as const, fontSize: '14px', color: theme.text, borderBottom: `1px solid ${theme.border}`, whiteSpace: 'nowrap' as const },
    tr: { transition: 'background 0.2s', cursor: 'pointer' },
    pagination: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px', borderTop: `1px solid ${theme.border}`, flexWrap: 'wrap' as const, gap: '12px' },
    paginationInfo: { fontSize: '14px', color: theme.textSecondary },
    paginationButtons: { display: 'flex', gap: '8px', alignItems: 'center' },
    pageButton: { padding: '8px 14px', fontSize: '13px', fontWeight: 500, border: `1px solid ${theme.inputBorder}`, borderRadius: '6px', background: theme.inputBg, color: theme.text, cursor: 'pointer' },
    pageButtonDisabled: { opacity: 0.5, cursor: 'not-allowed' },
    spinner: { width: '24px', height: '24px', border: '3px solid rgba(59, 130, 246, 0.3)', borderTop: '3px solid #3b82f6', borderRadius: '50%', animation: 'spin 1s linear infinite' },
  };

  const stats = [
    { label: 'Registered Players', value: registeredCount.toLocaleString(), subtext: 'Regular accounts' },
    { label: 'Guest Players', value: guestCount.toLocaleString(), subtext: 'Guest accounts' },
    { label: 'Online Now', value: onlinePlayers, subtext: 'Currently active' },
    { label: 'Total Games', value: totalGames.toLocaleString(), subtext: 'All game modes' },
    { label: 'Active (7 days)', value: activePlayers, subtext: 'Recent players' },
  ];

  const renderCell = (player: SlimPlayer, column: string) => {
    switch (column) {
      case 'username':
        return <span style={{ fontWeight: 500 }}>{player.username}</span>;
      case 'status':
        const statusColors = getStatusColor(player.status || '');
        return (
          <span style={{ padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: 600, background: statusColors.bg, color: statusColors.color, display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
            <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: statusColors.color }} />
            {player.status}
          </span>
        );
      case 'rankName':
        const rankColors = getRankColor(player.rankName || '');
        return (
          <div>
            <span style={{ padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: 600, background: rankColors.bg, color: rankColors.color }}>{player.rankName}</span>
            <div style={{ fontSize: '11px', color: theme.textSecondary, marginTop: '2px' }}>{player.rankScore} pts</div>
          </div>
        );
      case 'lastActivityTime':
        if (!player.lastActivityTime) return <span style={{ color: theme.textSecondary }}>-</span>;
        const date = new Date(player.lastActivityTime);
        const now = new Date();
        const diffMs = now.getTime() - date.getTime();
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);
        let timeAgo: string;
        if (diffMins < 1) timeAgo = 'Just now';
        else if (diffMins < 60) timeAgo = `${diffMins}m ago`;
        else if (diffHours < 24) timeAgo = `${diffHours}h ago`;
        else if (diffDays < 7) timeAgo = `${diffDays}d ago`;
        else timeAgo = date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
        return <span style={{ color: diffMins < 5 ? '#22c55e' : theme.text }}>{timeAgo}</span>;
      case 'actions':
        return (
          <button
            onClick={(e) => { e.stopPropagation(); navigate(`/Admin/Users/Players/${player.userId}`); }}
            style={{ padding: '6px 12px', fontSize: '12px', fontWeight: 600, background: '#22c55e', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
          >
            View
          </button>
        );
      default:
        return (player as any)[column] ?? '-';
    }
  };

  return (
    <div style={styles.container}>
      <style>{`
        @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
        @keyframes shimmer { 0% { left: -40%; } 100% { left: 100%; } }
      `}</style>

      <div style={styles.header}>
        <h1 style={styles.title}>Players</h1>
        <p style={styles.subtitle}>View and manage registered players</p>
      </div>

      <div style={styles.statsRow}>
        {stats.map((stat) => (
          <div key={stat.label} style={styles.statCard}>
            <div style={styles.statLabel}>{stat.label}</div>
            <div style={styles.statValue}>{stat.value}</div>
            <div style={styles.statSubtext}>{stat.subtext}</div>
          </div>
        ))}
      </div>

      <div style={styles.tableContainer}>
        <div style={styles.searchContainer}>
          <input
            type="text"
            placeholder="Search players by username..."
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            style={styles.searchInput}
          />
        </div>
        {!isClientSide && (
          <div style={{ position: 'relative', height: '3px', background: theme.border, overflow: 'hidden' }}>
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              height: '100%',
              width: '40%',
              background: 'linear-gradient(90deg, transparent, #3b82f6, transparent)',
              animation: 'shimmer 1.5s ease-in-out infinite',
            }} />
          </div>
        )}

        <div style={styles.tableWrapper}>
          <table style={styles.table}>
            <thead>
              <tr>
                {columns.map((col) => (
                  <th
                    key={col.key}
                    style={{ ...styles.th, width: col.width, cursor: col.sortable ? 'pointer' : 'default' }}
                    onClick={() => col.sortable && handleSort(col.key)}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                      {col.title}
                      {col.sortable && (
                        <span style={{ fontSize: '10px', opacity: sortColumn === col.key ? 1 : 0.5, color: sortColumn === col.key ? '#2563eb' : 'inherit' }}>
                          {sortColumn === col.key ? (sortOrder === 'asc' ? '▲' : sortOrder === 'desc' ? '▼' : '⇅') : '⇅'}
                        </span>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={columns.length} style={{ ...styles.td, textAlign: 'center', padding: '40px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                      <div style={styles.spinner} />
                      <span style={{ color: theme.textSecondary }}>Loading players...</span>
                    </div>
                  </td>
                </tr>
              ) : displayPlayers.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} style={{ ...styles.td, textAlign: 'center', padding: '40px', color: theme.textSecondary }}>
                    {searchTerm ? 'No players found matching your search' : 'No players available'}
                  </td>
                </tr>
              ) : (
                displayPlayers.map((player) => (
                  <tr
                    key={player.userId}
                    style={styles.tr}
                    onClick={() => navigate(`/Admin/Users/Players/${player.userId}`)}
                    onMouseEnter={(e) => { e.currentTarget.style.background = theme.tableRowHover; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    {columns.map((col) => (
                      <td key={col.key} style={{ ...styles.td, width: col.width }}>
                        {renderCell(player, col.key)}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {displayTotalCount > 0 && (
          <div style={styles.pagination}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
              <span style={styles.paginationInfo}>
                Showing {Math.min((currentPage - 1) * pageSize + 1, displayTotalCount)} - {Math.min(currentPage * pageSize, displayTotalCount)} of {displayTotalCount.toLocaleString()} players
              </span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '14px', color: theme.textSecondary }}>Per page:</span>
                <select
                  value={pageSize}
                  onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                  style={{
                    padding: '6px 10px',
                    fontSize: '13px',
                    border: `1px solid ${theme.inputBorder}`,
                    borderRadius: '6px',
                    background: theme.inputBg,
                    color: theme.text,
                    cursor: 'pointer',
                    outline: 'none',
                  }}
                >
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <option key={size} value={size}>{size}</option>
                  ))}
                </select>
              </div>
            </div>
            <div style={styles.paginationButtons}>
              <button
                style={{ ...styles.pageButton, ...(currentPage === 1 ? styles.pageButtonDisabled : {}) }}
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
              >
                First
              </button>
              <button
                style={{ ...styles.pageButton, ...(currentPage === 1 ? styles.pageButtonDisabled : {}) }}
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Previous
              </button>
              <span style={{ padding: '8px 14px', fontSize: '13px', color: theme.text }}>
                Page {currentPage} of {displayTotalPages.toLocaleString()}
              </span>
              <button
                style={{ ...styles.pageButton, ...(currentPage >= displayTotalPages ? styles.pageButtonDisabled : {}) }}
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage >= displayTotalPages}
              >
                Next
              </button>
              <button
                style={{ ...styles.pageButton, ...(currentPage >= displayTotalPages ? styles.pageButtonDisabled : {}) }}
                onClick={() => handlePageChange(displayTotalPages)}
                disabled={currentPage >= displayTotalPages}
              >
                Last
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PlayerListView;
